import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { CSSProperties, PointerEvent, RefObject } from "react";

interface SurfaceSize {
  width: number;
  height: number;
}

interface ResizeStart extends SurfaceSize {
  pointerId: number;
  clientX: number;
  clientY: number;
  maxWidth: number;
  maxHeight: number;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** Bottom-right resize behavior for a fixed floating surface. */
export function useFloatingSurfaceResize({
  open,
  resetKey,
  surfaceRef,
  defaultSize,
  minSize,
  viewportInset = 16,
}: {
  open: boolean;
  resetKey: string | null;
  surfaceRef: RefObject<HTMLDivElement | null>;
  defaultSize: SurfaceSize;
  minSize: SurfaceSize;
  viewportInset?: number;
}) {
  const resizeStartRef = useRef<ResizeStart | null>(null);
  const defaultWidth = defaultSize.width;
  const defaultHeight = defaultSize.height;
  const [size, setSize] = useState({
    width: defaultWidth,
    height: defaultHeight,
  });

  useLayoutEffect(() => {
    resizeStartRef.current = null;
    setSize({ width: defaultWidth, height: defaultHeight });
  }, [defaultHeight, defaultWidth, resetKey]);

  useEffect(() => {
    if (!open) {
      resizeStartRef.current = null;
      return;
    }
    const clampToViewport = () => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      setSize((current) => ({
        width: clamp(
          current.width,
          minSize.width,
          window.innerWidth - rect.left - viewportInset,
        ),
        height: clamp(
          current.height,
          minSize.height,
          window.innerHeight - rect.top - viewportInset,
        ),
      }));
    };
    window.addEventListener("resize", clampToViewport);
    return () => window.removeEventListener("resize", clampToViewport);
  }, [minSize.height, minSize.width, open, surfaceRef, viewportInset]);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      event.preventDefault();
      event.stopPropagation();
      resizeStartRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        width: rect.width,
        height: rect.height,
        maxWidth: Math.max(
          minSize.width,
          window.innerWidth - rect.left - viewportInset,
        ),
        maxHeight: Math.max(
          minSize.height,
          window.innerHeight - rect.top - viewportInset,
        ),
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [minSize.height, minSize.width, surfaceRef, viewportInset],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      const start = resizeStartRef.current;
      if (!start || start.pointerId !== event.pointerId) return;
      event.preventDefault();
      setSize({
        width: clamp(
          start.width + event.clientX - start.clientX,
          minSize.width,
          start.maxWidth,
        ),
        height: clamp(
          start.height + event.clientY - start.clientY,
          minSize.height,
          start.maxHeight,
        ),
      });
    },
    [minSize.height, minSize.width],
  );

  const onPointerEnd = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const start = resizeStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    resizeStartRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const style: CSSProperties = {
    width: Math.min(size.width, window.innerWidth - viewportInset * 2),
    height: Math.min(size.height, window.innerHeight - viewportInset * 2),
    minWidth: Math.min(minSize.width, window.innerWidth - viewportInset * 2),
    minHeight: Math.min(minSize.height, window.innerHeight - viewportInset * 2),
  };

  return {
    size,
    style,
    resizeHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onLostPointerCapture: () => {
        resizeStartRef.current = null;
      },
    },
  };
}
